import { supabase } from '@/lib/supabase/client';
import type { DisplayConfiguration, DisplayConfigurationInsert, DisplayConfigurationUpdate } from '@/types/display-config';

export const displayConfigService = {
  async getByLocationId(locationId: string): Promise<DisplayConfiguration[]> {
    try {
      const { data, error } = await supabase
        .from('display_configs')
        .select('*')
        .eq('location_id', locationId)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching display configs:', error);
        return [];
      }

      return (data as DisplayConfiguration[]) || [];
    } catch (error) {
      console.error('Error fetching display configs:', error);
      return [];
    }
  },

  async getByDisplayTypeId(displayTypeId: string): Promise<DisplayConfiguration[]> {
    try {
      const { data, error } = await supabase
        .from('display_configs')
        .select('*')
        .eq('display_type_id', displayTypeId)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching display configs by display type ID:', error);
        return [];
      }

      return (data as DisplayConfiguration[]) || [];
    } catch (error) {
      console.error('Error fetching display configs by display type ID:', error);
      return [];
    }
  },

  async getById(id: string): Promise<DisplayConfiguration | null> {
    try {
      const { data, error } = await supabase.from('display_configs').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching display config:', error);
        return null;
      }

      return (data as DisplayConfiguration) || null;
    } catch (error) {
      console.error('Error fetching display config:', error);
      return null;
    }
  },

  async create(data: DisplayConfigurationInsert): Promise<DisplayConfiguration> {
    try {
      const insertData = {
        display_type_id: data.display_type_id,
        location_id: data.location_id,
        sku_count: data.sku_count ?? null,
        screen_size: data.screen_size ?? null,
        screen_manufacturer: data.screen_manufacturer ?? null,
        mount_type: data.mount_type ?? null,
        is_custom: data.is_custom ?? false,
        partner_name: data.partner_name ?? null,
      };

      const { data: newConfig, error } = await supabase.from('display_configs').insert(insertData).select().single();

      if (error) {
        console.error('Error creating display config:', error);
        throw new Error(`Failed to create display config: ${error.message}`);
      }

      return newConfig as DisplayConfiguration;
    } catch (error) {
      console.error('Error creating display config:', error);
      throw error;
    }
  },

  async update(id: string, data: DisplayConfigurationUpdate): Promise<DisplayConfiguration> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.sku_count !== undefined) updateData.sku_count = data.sku_count;
      if (data.screen_size !== undefined) updateData.screen_size = data.screen_size;
      if (data.screen_manufacturer !== undefined) updateData.screen_manufacturer = data.screen_manufacturer;
      if (data.mount_type !== undefined) updateData.mount_type = data.mount_type;
      if (data.is_custom !== undefined) updateData.is_custom = data.is_custom;
      if (data.partner_name !== undefined) updateData.partner_name = data.partner_name;

      const { data: updatedConfig, error } = await supabase
        .from('display_configs')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating display config:', error);
        throw new Error(`Failed to update display config: ${error.message}`);
      }

      if (!updatedConfig) {
        throw new Error('Display config not found');
      }

      return updatedConfig as DisplayConfiguration;
    } catch (error) {
      console.error('Error updating display config:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('display_configs').delete().eq('id', id);

      if (error) {
        console.error('Error deleting display config:', error);
        throw new Error(`Failed to delete display config: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting display config:', error);
      throw error;
    }
  },
};
